import React, { useState } from 'react';
import { ChartBarIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';

interface HealthReportsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerateReport: (reportType: string, format: string) => Promise<void>;
}

const HealthReportsModal: React.FC<HealthReportsModalProps> = ({
  isOpen,
  onClose,
  onGenerateReport,
}) => {
  const [selectedReportType, setSelectedReportType] = useState('bird');
  const [selectedFormat, setSelectedFormat] = useState('pdf');
  const [loading, setLoading] = useState(false);

  const reportTypes = [
    {
      id: 'bird',
      name: 'Individual Bird Health Report',
      description: 'Comprehensive health report for a specific bird including test history, vaccinations, and treatments.',
      icon: '🐦',
    },
    {
      id: 'flock',
      name: 'Flock Health Report',
      description: 'Overall health status and statistics for the entire flock or specific groups.',
      icon: '🏠',
    },
    {
      id: 'analytics',
      name: 'Health Analytics Report',
      description: 'Advanced analytics including trends, patterns, and health insights over time.',
      icon: '📊',
    },
    {
      id: 'vaccination',
      name: 'Vaccination Compliance Report',
      description: 'Vaccination schedules, compliance rates, and upcoming vaccination requirements.',
      icon: '💉',
    },
    {
      id: 'treatment',
      name: 'Treatment Effectiveness Report',
      description: 'Analysis of treatment outcomes, medication effectiveness, and recovery rates.',
      icon: '💊',
    },
  ];

  const formats = [
    { id: 'pdf', name: 'PDF Document', icon: '📄' },
    { id: 'excel', name: 'Excel Spreadsheet', icon: '📊' },
    { id: 'csv', name: 'CSV Data', icon: '📋' },
  ];

  const handleGenerateReport = async () => {
    try {
      setLoading(true);
      await onGenerateReport(selectedReportType, selectedFormat);
      onClose();
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-4xl">
      <BrandModalHeader
        title={
          <span className="inline-flex items-center">
            <ChartBarIcon className="h-6 w-6 text-indigo-500 mr-3" />
            Health Reports
          </span>
        }
        onClose={onClose}
      />
      <BrandModalBody className="space-y-6">
                  {/* Report Type Selection */}
                  <div>
                    <h4 className="text-sm font-medium text-slate-900 mb-4">Select Report Type</h4>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {reportTypes.map((reportType) => (
                        <div
                          key={reportType.id}
                          className={`relative rounded-lg border p-4 cursor-pointer hover:bg-slate-50/80 ${
                            selectedReportType === reportType.id
                              ? 'border-cyan-500 ring-2 ring-cyan-500'
                              : 'border-gray-300'
                          }`}
                          onClick={() => setSelectedReportType(reportType.id)}
                        >
                          <div className="flex items-start">
                            <div className="text-2xl mr-3">{reportType.icon}</div>
                            <div className="flex-1">
                              <div className="flex items-center">
                                <input
                                  type="radio"
                                  name="reportType"
                                  value={reportType.id}
                                  checked={selectedReportType === reportType.id}
                                  onChange={() => setSelectedReportType(reportType.id)}
                                  className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300"
                                />
                                <label className="ml-3 block text-sm font-medium text-slate-900">
                                  {reportType.name}
                                </label>
                              </div>
                              <p className="mt-1 text-sm text-slate-500">{reportType.description}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Format Selection */}
                  <div>
                    <h4 className="text-sm font-medium text-slate-900 mb-4">Select Format</h4>
                    <div className="flex space-x-4">
                      {formats.map((format) => (
                        <div
                          key={format.id}
                          className={`flex items-center space-x-2 p-3 rounded-lg border cursor-pointer hover:bg-slate-50/80 ${
                            selectedFormat === format.id
                              ? 'border-cyan-500 bg-cyan-50'
                              : 'border-gray-300'
                          }`}
                          onClick={() => setSelectedFormat(format.id)}
                        >
                          <input
                            type="radio"
                            name="format"
                            value={format.id}
                            checked={selectedFormat === format.id}
                            onChange={() => setSelectedFormat(format.id)}
                            className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300"
                          />
                          <span className="text-xl">{format.icon}</span>
                          <span className="text-sm font-medium text-slate-900">{format.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Report Preview/Description */}
                  <div className="bg-slate-50/80 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-slate-900 mb-2">Report Details</h4>
                    <div className="text-sm text-slate-600">
                      {selectedReportType === 'bird' && (
                        <div>
                          <p><strong>Includes:</strong></p>
                          <ul className="list-disc list-inside mt-1 space-y-1">
                            <li>Individual bird identification and basic information</li>
                            <li>Complete health test history with results</li>
                            <li>Vaccination records and schedules</li>
                            <li>Treatment history and medication records</li>
                            <li>Health status timeline and trends</li>
                            <li>Upcoming health requirements</li>
                          </ul>
                        </div>
                      )}
                      {selectedReportType === 'flock' && (
                        <div>
                          <p><strong>Includes:</strong></p>
                          <ul className="list-disc list-inside mt-1 space-y-1">
                            <li>Overall flock health statistics</li>
                            <li>Health status distribution</li>
                            <li>Disease outbreak tracking</li>
                            <li>Mortality and morbidity rates</li>
                            <li>Vaccination compliance rates</li>
                            <li>Health alerts and recommendations</li>
                          </ul>
                        </div>
                      )}
                      {selectedReportType === 'analytics' && (
                        <div>
                          <p><strong>Includes:</strong></p>
                          <ul className="list-disc list-inside mt-1 space-y-1">
                            <li>Health trends and patterns over time</li>
                            <li>Seasonal health analysis</li>
                            <li>Treatment effectiveness metrics</li>
                            <li>Cost analysis for health management</li>
                            <li>Predictive health insights</li>
                            <li>Performance benchmarks</li>
                          </ul>
                        </div>
                      )}
                      {selectedReportType === 'vaccination' && (
                        <div>
                          <p><strong>Includes:</strong></p>
                          <ul className="list-disc list-inside mt-1 space-y-1">
                            <li>Vaccination schedules by bird and type</li>
                            <li>Compliance rates and overdue vaccinations</li>
                            <li>Vaccine effectiveness tracking</li>
                            <li>Side effects and adverse reactions</li>
                            <li>Upcoming vaccination requirements</li>
                            <li>Vaccination cost analysis</li>
                          </ul>
                        </div>
                      )}
                      {selectedReportType === 'treatment' && (
                        <div>
                          <p><strong>Includes:</strong></p>
                          <ul className="list-disc list-inside mt-1 space-y-1">
                            <li>Treatment outcomes and success rates</li>
                            <li>Medication effectiveness analysis</li>
                            <li>Recovery time statistics</li>
                            <li>Treatment cost analysis</li>
                            <li>Drug resistance patterns</li>
                            <li>Treatment recommendations</li>
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>

      </BrandModalBody>
      <BrandModalFooter>
        <button
          type="button"
          className="btn btn-secondary text-sm"
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleGenerateReport}
          disabled={loading}
          className="btn btn-primary text-sm inline-flex items-center disabled:opacity-50"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Generating...
            </>
          ) : (
            <>
              <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
              Generate Report
            </>
          )}
        </button>
      </BrandModalFooter>
    </BrandModal>
  );
};

export default HealthReportsModal; 